import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useDocumentStore } from '@/stores/useDocumentStore';
import { ProcessingIndicator } from './processing-indicator';
import { ProcessingSummary } from './processing-summary';
import { InitialEntities } from './initial-entities';
import { ShippingFlow } from './shipping-flow';
import { TransactionTimeline } from './transaction-timeline';
import { ErrorFallback } from '@/components/error-boundary/ErrorFallback';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs';
import { potentialMatchNames, unreadNameCount } from '@/domain/document-processing/utils/verification-outcome';
import { AlertTriangle, FileWarning, ScanText } from 'lucide-react';

function ProcessingResultsComponent({ documents, exportTarget }: {
  documents?: React.ReactNode;
  exportTarget?: HTMLElement | null;
}) {
  const [tab, setTab] = useState('parties');
  const state = useDocumentStore();
  const { 
    extractedEntities,
    results,
    currentReviewStage,
    isProcessing,
    consolidationResult,
    setShowConsolidatedEntities,
    showConsolidatedEntities
  } = state;
  const matches = potentialMatchNames(state).length;
  const unread = unreadNameCount(state);
  const failedFiles = new Set(results.filter(result => result.status === 'failed').map(result => result.documentId)).size;
  
  // Add breadcrumb when processing state changes
  useEffect(() => {
    Sentry.addBreadcrumb({
      category: 'processing',
      message: `Processing state update: ${isProcessing ? 'Started' : 'Completed'}`,
      level: 'info',
      data: {
        entitiesCount: extractedEntities.length,
        stage: currentReviewStage,
      }
    });
  }, [isProcessing, extractedEntities.length, currentReviewStage]);

  // When consolidation completes, automatically update the display
  useEffect(() => {
    if (consolidationResult && consolidationResult.consolidatedEntities.length > 0) {
      setShowConsolidatedEntities(true);
    }
  }, [consolidationResult, setShowConsolidatedEntities]);

  // Check if extended data is available for visualizations
  const hasExtendedData = consolidationResult && 'extendedData' in consolidationResult && consolidationResult.extendedData;
  const hasShippingFlow = hasExtendedData && consolidationResult.extendedData?.shippingFlow;
  const hasDocumentFlow = hasExtendedData && consolidationResult.extendedData?.documentFlow;
  const hasSummary = extractedEntities.length > 0 || results.length > 0 || !!consolidationResult;

  const triggerClass = 'shrink-0 gap-2 whitespace-nowrap rounded-none border-b-2 border-transparent px-4 py-3 leading-snug data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none';
  const panelClass = 'mt-5 space-y-4 data-[state=inactive]:hidden';

  return (
    <div className="space-y-4">
      {(isProcessing || !hasSummary) && <ProcessingIndicator />}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Order sections" className="flex h-auto w-full items-stretch justify-start overflow-x-auto rounded-none border-b bg-transparent p-0">
          <TabsTrigger value="parties" className={triggerClass}>
            Parties &amp; checks
            {(matches > 0 || unread > 0) && <span className="inline-flex items-center gap-2 text-xs font-medium tabular-nums">
              {matches > 0 && <span className="inline-flex items-center gap-1 text-destructive" title={`${matches} potential ${matches === 1 ? 'match' : 'matches'}`}>
                <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5" />
                <span aria-hidden="true">{matches}</span>
                <span className="sr-only">{matches} potential {matches === 1 ? 'match' : 'matches'}</span>
              </span>}
              {unread > 0 && <span className="inline-flex items-center gap-1 text-muted-foreground" title={`${unread} unreadable ${unread === 1 ? 'name' : 'names'}`}>
                <ScanText aria-hidden="true" className="h-3.5 w-3.5" />
                <span aria-hidden="true">{unread}</span>
                <span className="sr-only">{unread} unreadable {unread === 1 ? 'name' : 'names'}</span>
              </span>}
            </span>}
          </TabsTrigger>
          <TabsTrigger value="documents" className={triggerClass}>
            Documents
            {failedFiles > 0 && <span className="inline-flex items-center gap-1 text-xs font-medium tabular-nums text-destructive" title={`${failedFiles} failed ${failedFiles === 1 ? 'file' : 'files'}`}>
              <FileWarning aria-hidden="true" className="h-3.5 w-3.5" />
              <span aria-hidden="true">{failedFiles}</span>
              <span className="sr-only">{failedFiles} failed {failedFiles === 1 ? 'file' : 'files'}</span>
            </span>}
          </TabsTrigger>
          <TabsTrigger value="transaction" className={triggerClass}>Transaction &amp; shipping</TabsTrigger>
        </TabsList>
        {/* Mounted panels retain expansion/review state and the complete export content. */}
        <TabsContent value="parties" forceMount className={panelClass}>
          {!isProcessing && hasSummary && <ProcessingSummary />}
      
      {/* Show entities based on which view is selected */}
      {showConsolidatedEntities && consolidationResult && consolidationResult.consolidatedEntities.length > 0 ? (
        <InitialEntities 
          entities={consolidationResult.consolidatedEntities as any[]} 
          sectionTitle="Parties" 
          exportTarget={exportTarget}
        />
      ) : (
        extractedEntities.length > 0
        || (consolidationResult?.humanIdentityReviews?.length ?? 0) > 0
        || results.some(result =>
          (result.unresolvedVisualIdentities?.length ?? 0) > 0 || (result.humanIdentityReviews?.length ?? 0) > 0
        )
      ) && (
        <InitialEntities 
          entities={extractedEntities}
          sectionTitle="Parties" 
          exportTarget={exportTarget}
        />
      )}
      
        </TabsContent>
        <TabsContent value="documents" forceMount className={panelClass}>
          {documents || <p className="text-sm text-muted-foreground">No documents are available for this order.</p>}
        </TabsContent>
        <TabsContent value="transaction" forceMount className={panelClass}>
      {hasDocumentFlow && (
        <TransactionTimeline 
          documentFlow={consolidationResult.extendedData.documentFlow} 
          className="mt-6"
        />
      )}
      {!hasDocumentFlow && <section className="rounded-lg border p-4">
        <h2 className="font-semibold">Transaction timeline</h2>
        <p className="mt-1 text-sm text-muted-foreground">{isProcessing ? 'The timeline will appear here if it can be established from the documents.' : 'No transaction timeline is available for this order.'}</p>
      </section>}
      
      {hasShippingFlow && (
        <ShippingFlow 
          shippingFlow={consolidationResult.extendedData.shippingFlow} 
          className="mt-6"
        />
      )}
      {!hasShippingFlow && <section className="rounded-lg border p-4">
        <h2 className="font-semibold">Shipping flow</h2>
        <p className="mt-1 text-sm text-muted-foreground">{isProcessing ? 'Shipping information will appear here if it is found in the documents.' : 'No shipping information is available for this order.'}</p>
      </section>}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export const ProcessingResults = Sentry.withErrorBoundary(ProcessingResultsComponent, {
  fallback: ErrorFallback
});
